// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useTriggerImportMutation } from "@/api/hooks/use-imports";
import { ImportRetriggerIcon } from "@/components/icons";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export interface RetriggerImportTarget {
  importRequestId: number;
  importType: string;
  storeCode?: string;
}

interface RetriggerImportDialogProps {
  target: RetriggerImportTarget | null;
  onClose: () => void;
}

export function RetriggerImportDialog({ target, onClose }: RetriggerImportDialogProps) {
  const { t } = useTranslation("back-office");
  const triggerImport = useTriggerImportMutation();

  // Keep the last-seen target so dialog content doesn't blank out during the close animation.
  const lastTarget = useRef(target);
  if (target) lastTarget.current = target;
  const display = target ?? lastTarget.current;

  const [includeSubsequent, setIncludeSubsequent] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to target changes; triggerImport ref changes every render
  useEffect(
    function resetWhenOpened() {
      if (target) {
        setIncludeSubsequent(false);
        triggerImport.reset();
      }
    },
    [target],
  );

  async function handleConfirm() {
    if (!display) return;
    try {
      await triggerImport.mutateAsync({
        importId: display.importRequestId,
        includeSubsequent,
      });
      toast.success(t("imports.retrigger.success", { id: display.importRequestId }));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  function describeError(message: string): string {
    const contentMatch = message.match(/content_uri:(\d+)/);
    if (contentMatch) return t("imports.retrigger.errors.missingContent", { id: contentMatch[1] });
    const importMatch = message.match(/import_id:(\d+)/);
    if (importMatch) return t("imports.retrigger.errors.notFound", { id: importMatch[1] });
    return message;
  }

  return (
    <Dialog
      open={!!target}
      onOpenChange={(o) => {
        if (!o && !triggerImport.isPending) onClose();
      }}
    >
      <DialogContent id="dialog-retrigger-import" className="sm:max-w-[34rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={ImportRetriggerIcon} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("imports.retrigger.title")}</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-1">
                  <p>{t("imports.retrigger.prompt")}</p>
                  <p className="text-sm">
                    <span className="font-medium text-foreground">{display?.importType}</span>
                    {display?.storeCode && (
                      <>
                        {" · "}
                        <span className="font-mono text-foreground">{display.storeCode}</span>
                      </>
                    )}
                    {" · "}
                    <span className="font-mono text-foreground">#{display?.importRequestId}</span>
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="retrigger-include-subsequent" className="text-sm font-medium">
                  {t("imports.retrigger.includeSubsequent.label")}
                </Label>
                <p className="text-xs text-muted-foreground">{t("imports.retrigger.includeSubsequent.hint")}</p>
              </div>
              <Switch
                id="retrigger-include-subsequent"
                checked={includeSubsequent}
                onCheckedChange={setIncludeSubsequent}
                disabled={triggerImport.isPending}
              />
            </div>

            {triggerImport.isError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="min-w-0 space-y-0.5">
                  <p className="font-medium">{t("imports.retrigger.errorTitle")}</p>
                  {triggerImport.error?.message && (
                    <p className="text-xs text-destructive/90 break-words">
                      {describeError(triggerImport.error.message)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose} disabled={triggerImport.isPending}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={triggerImport.isPending}>
            {triggerImport.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImportRetriggerIcon className="h-4 w-4" />
            )}
            {t("imports.retrigger.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
